import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, filter, map, Observable, of, ReplaySubject, Subject, Subscription, takeUntil, tap, timer } from 'rxjs';
import { isNullOrUndefined } from 'src/app/shared/utils';

export type Debounced<R = void> = ((...args: any[]) => Observable<R>) & {
  now: (...args: any[]) => Observable<R>;
  cancel: () => void;
  dispose: () => void;
};

export interface DebouncedOptions {
  readonly destroyRef?: DestroyRef;
  readonly maxWait?: number;
}

export function debounced<R = void>(callback: (...args: any[]) => R, dueTime: number): Debounced<R>;
export function debounced<R = void>(callback: (...args: any[]) => R, dueTime: number, destroyRef: DestroyRef): Debounced<R>;
export function debounced<R = void>(callback: (...args: any[]) => R, dueTime: number, options: DebouncedOptions): Debounced<R>;
export function debounced<R = void>(callback: (...args: any[]) => R, dueTime: number, options?: DestroyRef | DebouncedOptions): Debounced<R> {
  if (options instanceof DestroyRef)
    options = { destroyRef: options };

  let destroyRef = options?.destroyRef;
  let maxWait = options?.maxWait;

  let subject = new Subject<void>();
  let value = new ReplaySubject<R>(1);

  let pending = false;
  let currentArgs = [];

  let maxWait$: Subscription;

  let handler = () => {
    if (!pending) 
      return;

    pending = false;

    try {
      maxWait$?.unsubscribe();
      maxWait$ = null;
    }
    catch (err) {
      setTimeout(() => { throw err; });
    }
    finally {
      let args = [...currentArgs];
      currentArgs = [];
      value.next(callback(...args));
    }
  };

  let debounce$ = subject.pipe(
    debounceTime(dueTime),
    tap(handler),
    catchError(err => {
      setTimeout(() => { throw err; });
      return of(null);
    })
  );

  if (destroyRef)
    debounce$ = debounce$.pipe(takeUntilDestroyed(destroyRef));

  let sub = debounce$.subscribe();

  let debounced: any = ((...args: any[]) => {
    pending = true;
    currentArgs = [...args];

    if (!subject.closed) {
      subject.next();
      if (!isNullOrUndefined(maxWait))
        maxWait$ = timer(maxWait).pipe(
          tap(handler),
          catchError(err => {
            setTimeout(() => { throw err; });
            return of(null);
          })
        ).subscribe();
    }

    return debounce$;
  });

  debounced.cancel = () => {
    pending = false;
    currentArgs = [];

    maxWait$?.unsubscribe();
    maxWait$ = null;
  };

  debounced.dispose = () => {
    debounced.cancel();
    subject.complete();
    sub.unsubscribe();
  };

  debounced.now = (...args: any[]) => {
    debounced.cancel();
    currentArgs = args;
    pending = true;
    handler();
    return debounce$;
  };

  return debounced;
}
